import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ContentLayout, SpaceBetween } from "@amzn/awsui-components-react-v3";
import { useAuth } from "react-oidc-context";
import { useParams, useSearchParams } from "react-router-dom";
import { PageLayout } from "../components/page-layout";
import { useGetUserQuery } from "../services/idp/idp";
import { CredentialsTable } from "../components/credentials-table";
import { UserOverview } from "../components/user-overview";
import { GroupsOverview } from "../components/groups-overview";
import { UserProfileHeader } from "../components/user-profile-header";
import { formatMessageFromResponseErrorData } from "../utils/errors";
import { isAuthedUserAdmin, isAuthedUserManager } from "../utils/permissions";
import { Alerts } from "../components/alerts";
import { Alerts as AlertsType } from "../lib/types";
import { PagePaths } from "../lib/constants";
import { Breadcrumbs } from "../components/breadcrumbs";
import { formatRoute } from "../utils/route";
import { sanitizeAlias } from "../utils/input-validation";

export const UserProfile = () => {
  const auth = useAuth();
  const { alias: aliasParam } = useParams<{ alias: string }>();
  const [searchParams] = useSearchParams();
  const refPageParam = searchParams.get("ref");
  const loggedInUserAlias =
    (auth.user?.profile?.preferred_username as string) ?? "";
  const profileAlias =
    aliasParam === "me"
      ? loggedInUserAlias
      : aliasParam
        ? sanitizeAlias(aliasParam)
        : "";
  const isSelf = profileAlias === loggedInUserAlias;
  const isManager = isAuthedUserManager(auth);
  const isAdmin = isAuthedUserAdmin(auth);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<React.ReactNode>(null);
  const [alerts, setAlerts] = useState<AlertsType>({});
  const { data, isFetching, isLoading, isError, error, refetch } =
    useGetUserQuery(profileAlias, { skip: !profileAlias });
  const [aliasChanged, setAliasChanged] = useState(false);

  const breadcrumbItems = useMemo(() => {
    const directsPageRef =
      refPageParam === PagePaths.MANAGE_DIRECTS
        ? [{ href: PagePaths.MANAGE_DIRECTS, text: "Your directs" }]
        : [];
    return [
      { href: "#", text: "User Management" },
      ...directsPageRef,
      {
        href: formatRoute(PagePaths.USER_PROFILE, {
          routeParams: { alias: profileAlias },
        }),
        text: profileAlias,
      },
    ];
  }, [profileAlias, refPageParam]);

  // clear alerts and set aliasChanged to true when the current alias changes
  useEffect(() => {
    setAlerts({});
    setAliasChanged(true);
  }, [profileAlias]);

  // when the alias changes and the user query refetch completes, set aliasChanged back to false
  useEffect(() => {
    if (aliasChanged && !isFetching) {
      setAliasChanged(false);
    }
  }, [aliasChanged, isFetching]);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    if (isError) {
      setAlerts((prev) => ({
        ...prev,
        fetchUserError: {
          message: formatMessageFromResponseErrorData((error as any)?.data),
          ariaLabel: "Error",
          type: "error",
        },
      }));
      return;
    }
    // clear any previous fetch user error
    setAlerts((prev) => {
      const { fetchUserError, ...rest } = prev;
      return rest;
    });
  }, [isFetching, isError, error]);

  const openHelpPanel = (panel: React.ReactNode) => {
    setActivePanel(panel);
    setToolsOpen(true);
  };

  const handleDismissAlert = useCallback((key: string) => {
    setAlerts((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const user = data?.user;
  const groups = data?.groups;

  return (
    <PageLayout
      auth={auth}
      tools={activePanel}
      toolsOpen={toolsOpen}
      onToolsChange={setToolsOpen}
    >
      <ContentLayout
        headerVariant="high-contrast"
        header={
          <UserProfileHeader
            user={user}
            showSkeleton={isLoading || isError || (aliasChanged && isFetching)}
            showActionsDropdown={isAdmin || (isManager && !isSelf)}
            allowRefresh
            onRefresh={refetch}
            refreshing={isFetching}
          />
        }
        defaultPadding
        notifications={<Alerts items={alerts} onDismiss={handleDismissAlert} />}
        breadcrumbs={<Breadcrumbs items={breadcrumbItems} />}
      >
        <SpaceBetween size="l">
          <UserOverview
            user={user}
            showSkeleton={isLoading || isFetching || isError}
            onInfoClick={openHelpPanel}
          />
          <CredentialsTable
            key={user?.alias}
            credentialsInfo={user?.credentialsInfo ?? {}}
            alias={user?.alias}
            isSelf={isSelf}
            showSkeleton={isLoading || isFetching || isError}
            setAlerts={setAlerts}
            onInfoClick={openHelpPanel}
          />
          <GroupsOverview
            groups={groups}
            showSkeleton={isLoading || isFetching || isError}
            onInfoClick={openHelpPanel}
          />
        </SpaceBetween>
      </ContentLayout>
    </PageLayout>
  );
};
